import { observable, action, makeObservable, runInAction, computed } from 'mobx';
import type {
  Message, ChatResponseItem, ChatResponse, MessageResponse,
  OllamaStreamResponse, Model, ChatResponseItemSettings,
  ToolProgressUpdate, ToolApprovalDecision
} from '../models.ts';
import { getAttachmentType } from '../models.ts';
import { PromiseCompletion } from '../classes/PromiseCompletion.ts';
import apiService from './apiService.ts';
import messageService from './messageService.ts';
import { userService } from './userService.ts';
import { helperService } from './helperService.ts';
import { imageService } from './imageService.ts';
import { wsService } from './wsService.ts';
import type ModelSelector from '@components/ModelSelector.tsx';
import type Sidebar from '@pages/Chat/Sidebar.tsx';

type PendingToolApprovalInfo = {
  decision: ToolApprovalDecision | null;
  resolve: (d: ToolApprovalDecision) => void;
};

export type ChatSession = {
  loading: PromiseCompletion;
  messagesLoader: PromiseCompletion;
  currentMessage: Message | null;
  pendingToolApprovals: Map<string, PendingToolApprovalInfo>;
  selectedMessageId: string | null;
  generatingMessageId: string | null;
  showFollowUps: boolean;
  followUpKey: number;
  chatSettings: ChatResponseItemSettings | null;
  isAlternativePromptMode: boolean;
  currentModel: Model | null;
  /** WakeLock held during generation */
  wakeLock: WakeLockSentinel | null;
  /** Resolve function to end the loading state when generation completes */
  _generatingResolve: (() => void) | null;
  /** True while context compaction is in progress */
  isCompacting?: boolean;
};

class ChatExecutionService {
  @observable activeChatId: string | null = null;
  /** Which chats currently have an active generation – used for sidebar animation */
  @observable generatingChatIds: Set<string> = observable.set<string>();

  /** Legacy compat: returns the first generating chatId or null */
  @computed get generatingChatId(): string | null {
    if (this.generatingChatIds.size === 0) return null;
    return this.generatingChatIds.values().next().value ?? null;
  }

  /** Check if a specific chat is currently generating */
  isGenerating(chatId: string | null): boolean {
    return chatId ? this.generatingChatIds.has(chatId) : false;
  }
  /** Globally-selected model (mirrors the ModelSelector value) */
  @observable currentModel: Model | null = null;
  @observable chats: ChatResponseItem[] = [];
  @observable archivedChats: ChatResponseItem[] = [];
  @observable showArchivedChats: boolean = false;
  @observable hasMoreChats: boolean = false;
  @observable chatsLoader = new PromiseCompletion();

  private readonly _CHATS_PAGE_SIZE = 200;
  private _chatsPage: number = 0;
  private _isLoadingMoreChats: boolean = false;

  // Conversations keyed by chatId (or 'new' for a brand-new chat)
  private _conversations = observable.map<string, Message[]>({}, { deep: false });
  // Sessions keyed identically
  private _sessions = observable.map<string, ChatSession>({}, { deep: false });

  // UI callbacks registered by the Chat component
  private _autoScrollCallback: ((opts?: { force?: boolean; forceBottom?: boolean }) => void) | null = null;
  private _savePromptCallback: (() => void) | null = null;
  private _refreshModelsCallback: (() => void) | null = null;

  constructor() {
    makeObservable(this);
    this._setupWsListeners();
  }

  /** Called by Chat component on mount so the service can trigger DOM side-effects */
  registerCallbacks(callbacks: {
    autoScroll: (opts?: { force?: boolean; forceBottom?: boolean }) => void;
    savePrompt: () => void;
    refreshModels: () => void;
  }) {
    this._autoScrollCallback = callbacks.autoScroll;
    this._savePromptCallback = callbacks.savePrompt;
    this._refreshModelsCallback = callbacks.refreshModels;
  }

  // ─── Session / conversation helpers ───────────────────────────────────────

  getConversation(chatId: string | null): Message[] {
    const key = chatId || 'new';
    if (!this._conversations.has(key)) {
      this._conversations.set(key, observable.array<Message>([]));
    }
    return this._conversations.get(key)!;
  }

  @computed get activeConversation(): Message[] {
    return this.getConversation(this.activeChatId);
  }

  getSession(chatId: string | null): ChatSession {
    const key = chatId || 'new';
    if (!this._sessions.has(key)) {
      this._sessions.set(key, observable({
        loading: new PromiseCompletion(),
        messagesLoader: new PromiseCompletion(),
        currentMessage: null as Message | null,
        pendingToolApprovals: new Map<string, { decision: ToolApprovalDecision | null; resolve: (d: ToolApprovalDecision) => void }>(),
        selectedMessageId: null as string | null,
        generatingMessageId: null as string | null,
        showFollowUps: userService.showFollowUps,
        followUpKey: 0,
        chatSettings: null as ChatResponseItemSettings | null,
        isAlternativePromptMode: false,
        currentModel: null as Model | null,
        wakeLock: null as WakeLockSentinel | null,
        _generatingResolve: null as (() => void) | null,
      }));
    }
    return this._sessions.get(key)!;
  }

  @computed get activeSession(): ChatSession {
    return this.getSession(this.activeChatId);
  }

  // ─── Chat list management ─────────────────────────────────────────────────

  /** Timestamp (ms) recorded just before the last successful full or incremental chat list fetch. */
  private _lastChatsLoadedAt: number = 0;

  @action
  async loadChats(openingFirstChat: boolean = true, incremental: boolean = false): Promise<void> {
    // Use incremental fetch only after the first full load has completed
    if (incremental && this._lastChatsLoadedAt > 0) {
      const requestTime = Date.now();
      const resp = await apiService.get<ChatResponse>(`/api/chats?since=${this._lastChatsLoadedAt}`, { loader: this.chatsLoader });
      this._lastChatsLoadedAt = requestTime;
      if (resp.partial && resp.chats.length > 0) {
        runInAction(() => {
          const changedIds = new Set(resp.chats.map(c => c.id));
          const merged = [
            ...resp.chats,
            ...this.chats.filter(c => !changedIds.has(c.id)),
          ].sort((a, b) => b.modifiedAt - a.modifiedAt);
          this.chats = merged;
        });
      }
      return;
    }

    this._chatsPage = 0;
    const requestTime = Date.now();
    const resp = await apiService.get<ChatResponse>(`/api/chats?limit=${this._CHATS_PAGE_SIZE}&skip=0`, { loader: this.chatsLoader });
    this._lastChatsLoadedAt = requestTime;
    runInAction(() => {
      this.chats = resp.chats;
      this.hasMoreChats = resp.hasMore ?? false;
      if (this.chats.length > 0 && openingFirstChat && this.generatingChatIds.size === 0) {
        // Only auto-open first chat if nothing is generating
        const chat = this.chats[0];
        if (!this.activeChatId) {
          this.activeChatId = chat.id;
          this._loadChatMessagesInternal(chat.id, {});
        }
        this._cleanUpOldPrompts();
      }
    });
  }

  @action
  async loadMoreChats(): Promise<void> {
    if (!this.hasMoreChats || this._isLoadingMoreChats) return;
    this._isLoadingMoreChats = true;
    try {
      this._chatsPage++;
      const skip = this._chatsPage * this._CHATS_PAGE_SIZE;
      const resp = await apiService.get<ChatResponse>(`/api/chats?limit=${this._CHATS_PAGE_SIZE}&skip=${skip}`, { loader: this.chatsLoader });
      runInAction(() => {
        this.chats = [...this.chats, ...resp.chats];
        this.hasMoreChats = resp.hasMore ?? false;
      });
    } finally {
      this._isLoadingMoreChats = false;
    }
  }

  @action
  async loadArchivedChats(): Promise<void> {
    const resp = await apiService.get<ChatResponse>('/api/chats?archived=true', { loader: this.chatsLoader });
    runInAction(() => { 
      this.archivedChats = resp.chats; 
    });
  }

  @action
  async toggleArchivedChats(): Promise<void> {
    this.showArchivedChats = !this.showArchivedChats;
    if (this.showArchivedChats) {
      await this.loadArchivedChats(); 
    }
  }

  private _cleanUpOldPrompts() {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('promptInput_')) {
        const chatId = key.substring('promptInput_'.length);
        if (!this.chats.find(c => c.id === chatId) && chatId !== 'new') {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  // ─── Chat message loading / switching ────────────────────────────────────

  /** Main entry-point for selecting a chat (from sidebar click, URL, etc.) */
  @action
  async loadChatMessages(
    chatId: string,
    opts: { modelSelectorRef?: React.RefObject<ModelSelector | null>; sidebarRef?: React.RefObject<Sidebar | null>; onCheckModelUpdate?: (model: string) => void } = {}
  ): Promise<void> {
    if (!chatId) return;

    // If this is the chat currently generating, just switch the visible view to it
    if (this.isGenerating(chatId)) {
      runInAction(() => { this.activeChatId = chatId; });
      const chat = this.chats.find(c => c.id === chatId);
      if (chat?.model && opts.modelSelectorRef?.current) {
        opts.modelSelectorRef.current.trySetModel(chat.model);
      }
      if (window.innerWidth < 768 && opts.sidebarRef?.current) {
        opts.sidebarRef.current.close();
      }
      setTimeout(() => this._autoScrollCallback?.({ force: true }), 100);
      return;
    }

    await this._loadChatMessagesInternal(chatId, opts);

    const chat = this.chats.find(c => c.id === chatId);
    if (chat?.model && opts.onCheckModelUpdate) {
      opts.onCheckModelUpdate(chat.model);
    }
  }

  /** Load a non-active branch on demand (called when the user clicks a stub sibling message). */
  async loadBranch(chatId: string | null, selectedMessageId: string): Promise<void> {
    if (!chatId) return;
    if (this.isGenerating(chatId)) return;
    await this._loadChatMessagesInternal(chatId, {}, selectedMessageId);
  }

  @action.bound
  private async _loadChatMessagesInternal(
    chatId: string,
    opts: { modelSelectorRef?: React.RefObject<ModelSelector | null>; sidebarRef?: React.RefObject<Sidebar | null> },
    selectedMessageId?: string
  ): Promise<void> {
    if (!chatId) return;

    runInAction(() => { 
      this.activeChatId = chatId; 
    });
    const session = this.getSession(chatId);

    // Guard against concurrent loads (e.g. rapid stub navigation clicks)
    if (session.messagesLoader.isPending) return;

    session.showFollowUps = false;

    const chat = this.chats.find(c => c.id === chatId);
    if (chat?.model && opts.modelSelectorRef?.current) {
      opts.modelSelectorRef.current.trySetModel(chat.model);
    }
    session.isAlternativePromptMode = chat?.startFromBeginning || false;

    if (window.innerWidth < 768 && opts.sidebarRef?.current) {
      opts.sidebarRef.current.close();
    }

    const url = selectedMessageId ?? session.selectedMessageId
      ? `/api/chats/${chatId}/messages?mainThread=true&selectedMessageId=${encodeURIComponent((selectedMessageId ?? session.selectedMessageId)!)}`
      : `/api/chats/${chatId}/messages?mainThread=true`;
    const chatMessages = await apiService.get<MessageResponse>(url, { loader: session.messagesLoader });

    if (chat?.hasUnreadMessages) {
      apiService.post(`/api/chats/${chatId}/mark-read`).catch(() => {});
      runInAction(() => {
        const c = this.chats.find(x => x.id === chatId);
        if (c) c.hasUnreadMessages = false;
      });
    }

    runInAction(() => {
      const conv = this.getConversation(chatId);
      conv.length = 0;
      conv.push(...chatMessages.messages);

      // If the server reports an active generation for this chat, restore from cached state
      const rm = chatMessages.runningMessage;
      if (rm?.messageId) {
        this.generatingChatIds.add(chatId);
        const existingMsg = conv.find(m => m.id === rm.messageId);
        if (existingMsg) {
          // Merge server's in-memory content with what's in DB
          // Server content is always more up-to-date than DB for in-progress messages
          if (rm.content) existingMsg.content = rm.content;
          if (rm.thinking) existingMsg.thinking = rm.thinking;
          existingMsg.modelName = rm.modelName;
          existingMsg.lastTokenTimestamp = rm.lastTokenTimestamp;
          existingMsg._tokenBuffer = [];
          if (rm.toolCalls) {
            existingMsg.toolCalls = rm.toolCalls;
            existingMsg.toolsFunctionName = rm.toolCalls[0]?.name;
            existingMsg.toolsFunctionArguments = rm.toolCalls[0]?.arguments;
          }
          if (rm.modelLoadCompleted !== undefined) existingMsg.modelLoadCompleted = rm.modelLoadCompleted;
          if (rm.modelLoadTotal !== undefined) existingMsg.modelLoadTotal = rm.modelLoadTotal;
          if (rm.promptEvalCompleted !== undefined) existingMsg.promptEvalCompleted = rm.promptEvalCompleted;
          if (rm.promptEvalTotal !== undefined) existingMsg.promptEvalTotal = rm.promptEvalTotal;
          if (rm.waitingInfo) existingMsg.waitingInfo = rm.waitingInfo;
          session.currentMessage = existingMsg;
          session.generatingMessageId = existingMsg.id;
        } else {
          // Message not yet in DB response (just created), add it
          const newMsg: Message = {
            id: rm.messageId, role: 'assistant',
            content: rm.content || '', thinking: rm.thinking || '',
            timestamp: this.getTimestamp(chatId),
            modelName: rm.modelName,
            lastTokenTimestamp: rm.lastTokenTimestamp,
            _tokenBuffer: [],
          };
          if (rm.toolCalls) {
            newMsg.toolCalls = rm.toolCalls;
            newMsg.toolsFunctionName = rm.toolCalls[0]?.name;
            newMsg.toolsFunctionArguments = rm.toolCalls[0]?.arguments;
          }
          if (rm.modelLoadCompleted !== undefined) newMsg.modelLoadCompleted = rm.modelLoadCompleted;
          if (rm.modelLoadTotal !== undefined) newMsg.modelLoadTotal = rm.modelLoadTotal;
          if (rm.promptEvalCompleted !== undefined) newMsg.promptEvalCompleted = rm.promptEvalCompleted;
          if (rm.promptEvalTotal !== undefined) newMsg.promptEvalTotal = rm.promptEvalTotal;
          if (rm.waitingInfo) newMsg.waitingInfo = rm.waitingInfo;
          conv.push(newMsg);
          session.currentMessage = conv[conv.length - 1];
          session.generatingMessageId = rm.messageId;
        }
        // Start loading state (guard against double-subscribe from _onRestoreState / loadChats race:
        // if one already subscribed and stored _generatingResolve, skip — chat:done will resolve it)
        if (!session._generatingResolve) {
          session.loading.subscribe(new Promise<void>((resolve) => { session._generatingResolve = resolve; }));
        }

        // Handle tool progress restore
        if (chatMessages.runningToolProgress) {
          const tp = chatMessages.runningToolProgress;
          const toolMsg = [...conv].reverse().find(m => m.role === 'tool' && m.toolProgress?.operationId === tp.operationId);
          if (toolMsg) session.generatingMessageId = toolMsg.id;
        }
      } else {
        // No running generation — clear any stale generating indicator and keep streaming message if present
        this.generatingChatIds.delete(chatId);
        if (session.currentMessage?.id) {
          const idx = conv.findIndex(m => m.id === session.currentMessage!.id);
          if (idx !== -1) conv[idx] = session.currentMessage!;
        }
      }

      // Apply any WS tokens that arrived after the API snapshot
      if (session.currentMessage?._tokenBuffer?.length && session.currentMessage.lastTokenTimestamp) {
        const cachedTs = session.currentMessage.lastTokenTimestamp;
        const newTokens = session.currentMessage._tokenBuffer.filter(t => t.ts > cachedTs);
        // These are already applied by the WS handler, no need to re-apply content
        // Just ensure lastTokenTimestamp is up to date
        if (newTokens.length > 0) {
          session.currentMessage.lastTokenTimestamp = newTokens[newTokens.length - 1].ts;
        }
      }

      // When loading a specific branch (stub navigation), anchor the session to that branch
      // so getMessageChain() doesn't fall back to max-timestamp heuristic and accidentally
      // pick a sibling stub with a newer timestamp → empty/wrong thread display.
      if (selectedMessageId) {
        session.selectedMessageId = selectedMessageId;
      }

      setTimeout(() => this._autoScrollCallback?.({ force: true }), 100);
    });
  }

  // ─── Model ────────────────────────────────────────────────────────────────

  @action
  setCurrentModel(model: Model | null) {
    this.currentModel = model;
    const session = this.getSession(this.activeChatId);
    session.currentModel = model;
  }

  // ─── Upcoming (steering) messages ────────────────────────────────────────

  /** Queued steering messages for a chat, oldest first. Rendered in the "Upcoming"
   *  section; excluded from getMessageChain. */
  getUpcomingMessages(chatId: string | null): Message[] {
    return this.getConversation(chatId).filter(m => m.isUpcoming);
  }

  /**
   * Queue a steering ("upcoming") user message for a running generation. The message
   * is shown immediately in the Upcoming section and is picked up by the server loop
   * at the end of the current LLM round. `tempId` is the optimistic local id that will
   * be reconciled when the server echoes it back via `chat:upcoming`.
   *
   * Note: the server only emits `chat:done` at the very end of the loop (after all
   * queued steering is consumed), so the client's loading/generating state is already
   * active and must NOT be re-armed here.
   */
  @action
  steerMessage(chatId: string | null, content: string, tempId: string): void {
    if (!chatId) return;
    const parentMessage = this.getMessageChain(chatId).slice(-1)[0];
    const message: Message = {
      id: tempId,
      parentId: parentMessage?.id,
      role: 'user',
      content,
      timestamp: this.getTimestamp(chatId),
      isUpcoming: true,
    };
    this.getConversation(chatId).push(message);
    wsService.send('chat:steer', {
      chatId,
      message: content,
      tempMessageId: tempId,
      clientId: wsService.clientId,
    });
  }

  /**
   * Remove a queued steering message. Sent to the server, which deletes the
   * message if it is still upcoming (already-taken messages are kept). The
   * local splice is optimistic; the server's chat:upcoming_removed event is
   * idempotent for the same effect.
   */
  @action
  removeUpcomingMessage(chatId: string | null, messageId: string): void {
    if (!chatId) return;
    const conv = this.getConversation(chatId);
    const idx = conv.findIndex(m => m.id === messageId);
    if (idx !== -1) conv.splice(idx, 1);
    wsService.send('chat:upcoming_remove', { chatId, messageId });
  }

  /**
   * Edit a queued steering message: remove it from the queue (and ask the
   * server to delete the DB copy) and return its content so the caller can
   * place it back into the prompt editor. The edited version is re-sent as a
   * new steering message when the user sends it.
   */
  @action
  editUpcomingMessage(chatId: string | null, messageId: string): string | null {
    if (!chatId) return null;
    const conv = this.getConversation(chatId);
    const idx = conv.findIndex(m => m.id === messageId);
    if (idx === -1) return null;
    const [msg] = conv.splice(idx, 1);
    wsService.send('chat:upcoming_remove', { chatId, messageId });
    return msg.content;
  }

  // ─── Message chain (formerly Chat.getMessageChain) ─────────────────────

  getMessageChain(chatId: string | null, includeDefaultSystemPrompt = false, collapseThinking = false): Message[] {
    const session = this.getSession(chatId);
    // Exclude "upcoming" (steering) messages: they are queued for the running loop and
    // must not participate in active-branch resolution (their fresh timestamp would
    // otherwise hijack the max-timestamp leaf heuristic). Rendered separately.
    const conversation = this.getConversation(chatId).filter(m => !m.isUpcoming);
    const hasSystemMessage = conversation.length > 0 && conversation[0].role === 'system';
    const defaultSystemPrompt = userService.systemPrompt || '';
    const importantInfo = userService.importantInfo || '';
    let result = messageService.getMessageChain(conversation, session.selectedMessageId);

    if (!hasSystemMessage && (defaultSystemPrompt || importantInfo) && includeDefaultSystemPrompt) {
      const firstMessage = result[0];
      let systemContent = '';
      if (defaultSystemPrompt) systemContent = defaultSystemPrompt;
      if (importantInfo) systemContent += (systemContent ? '\n\n' : '') + '# Important context about the user:\n' + importantInfo;
      result.unshift({ id: 'sd', role: 'system', content: systemContent, timestamp: 0 });
      if (firstMessage) firstMessage.parentId = 'sd';
    }

    // Interleave multi-tool-call assistant messages with their results:
    // [assistant[tc1,tc2,tc3], tool1, tool2, tool3] → [assistant[tc1], tool1, assistant[tc2], tool2, assistant[tc3], tool3]
    // Only in expanded (non-collapse) mode; collapseThinking folds the whole sequence anyway.
    if (!collapseThinking) {
      const interleaved: Message[] = [];
      for (let i = 0; i < result.length; i++) {
        const msg = result[i];
        if (msg.role === 'assistant' && msg.toolCalls && msg.toolCalls.length > 1) {
          const toolResults: Message[] = [];
          let j = i + 1;
          while (j < result.length && result[j].role === 'tool') { toolResults.push(result[j]); j++; }
          if (toolResults.length > 1) {
            for (let k = 0; k < msg.toolCalls.length; k++) {
              const tc = msg.toolCalls[k];
              const matchingResult = toolResults.find(r => r.toolCallId === tc.id) ?? toolResults[k];
              if (!matchingResult) continue;
              if (k === 0) {
                // First virtual: keep LLM content/thinking; replace toolCalls with single entry
                interleaved.push({ ...msg, toolCalls: [tc], toolsFunctionName: tc.name, toolsFunctionArguments: tc.arguments });
              } else {
                // Subsequent virtuals: tool-call-only shell, no duplicate content/stats
                interleaved.push({ ...msg, id: `${msg.id}-tc${k}`, content: '', thinking: '',
                  toolCalls: [tc], toolsFunctionName: tc.name, toolsFunctionArguments: tc.arguments,
                  tokensIn: undefined, tokensOut: undefined, timeTaken: undefined, thinkingTimeTaken: undefined });
              }
              interleaved.push(matchingResult);
            }
            i = j - 1; // skip the tool result messages we've already handled
            continue;
          }
        }
        interleaved.push(msg);
      }
      result = interleaved;
    }

    if (collapseThinking) {
      for (let i = 0; i < result.length; i++) {
        const msg = result[i];
        if (msg.role === 'assistant' && (msg.toolsFunctionName || (msg.toolCalls && msg.toolCalls.length > 0))) {
          const collapsed: Message[] = [msg];
          for (let j = i + 1; j < result.length; j++) {
            const next = result[j];
            const shouldStop = next.role === 'user' || next.role === 'context' || j === result.length - 1;
            if ((next.role !== 'user' || j === result.length - 1) && next.role !== 'context') collapsed.push(next);
            if (shouldStop && collapsed.length > 1) {
              const final = { ...collapsed[collapsed.length - 1] };
              final.id = msg.id;
              final.tokensIn = collapsed.reduce((s, m) => s + (m.tokensIn || 0), 0);
              final.tokensOut = collapsed.reduce((s, m) => s + (m.tokensOut || 0), 0);
              final.timeTaken = collapsed.reduce((s, m) => s + (m.timeTaken || 0), 0);
              final.thinkingTimeTaken = collapsed.reduce((s, m) => s + (m.thinkingTimeTaken || 0), 0);
              final.collapsedMessages = collapsed;
              const reverseCollapsed = [...collapsed].reverse();
              final.modelName = reverseCollapsed.find(m => m.role !== 'tool' && m.modelName)?.modelName || final.modelName;
              const lastCall = reverseCollapsed.find(m => m.toolsFunctionName || (m.toolCalls && m.toolCalls.length > 0));
              if (lastCall) { final.toolsFunctionName = lastCall.toolsFunctionName; final.toolsFunctionArguments = lastCall.toolsFunctionArguments; final.toolCalls = lastCall.toolCalls; }
              const lastTool = reverseCollapsed.find(m => m.role === 'tool');
              if (lastTool) { final.errorMessage = lastTool.errorMessage; final.isAborted = lastTool.isAborted; final.parent = lastTool.parent; }
              const allSources = collapsed.filter(m => m.role === 'tool' && m.sources?.length).flatMap(m => m.sources!);
              const seenLinks = new Set<string>();
              const deduped = allSources.filter(s => { if (seenLinks.has(s.link)) return false; seenLinks.add(s.link); return true; });
              if (deduped.length > 0) final.sources = deduped;
              result.splice(i, collapsed.length, final);
              break;
            }
          }
        }
      }
    }

    return result;
  }

  // ─── Timestamp helper ─────────────────────────────────────────────────────

  getTimestamp(chatId: string | null): number {
    const conv = this.getConversation(chatId);
    const now = +Date.now();
    const last = conv.reduce((max, m) => Math.max(max, m.timestamp || 0), 0);
    return now <= last ? last + 1 : now + 1;
  }

  // ─── Abort helpers ────────────────────────────────────────────────────────

  abortSession(chatId: string | null): void {
    // Send cancel signal to server via WebSocket with chatId
    wsService.send('chat:cancel', { chatId });
    // Also resolve any pending tool approvals with 'deny'
    const session = this.getSession(chatId);
    session.pendingToolApprovals.forEach((approval) => {
      approval.decision = 'deny';
      approval.resolve('deny');
    });
    session.pendingToolApprovals.clear();
  }

  // ─── Message ID migration (new-chat → real chatId) ───────────────────────

  migrateNewSession(newChatId: string) {
    // Move conversation + session from 'new' key to real chatId
    const conv = this._conversations.get('new');
    if (conv) { 
      this._conversations.set(newChatId, conv); 
      this._conversations.delete('new'); 
    }
    const sess = this._sessions.get('new');
    if (sess) { 
      this._sessions.set(newChatId, sess); 
      this._sessions.delete('new'); 
    }
    return !!(conv || sess);
  }

  // ─── Message-ID update helper ─────────────────────────────────────────────

  private _updateMessageId(conv: Message[], oldId: string, newId: string): Message | null {
    const msg = conv.find(m => m.id === oldId);
    const children = conv.filter(m => m.parentId === oldId);
    if (msg) { 
      msg.id = newId; 
      children.forEach(c => { c.parentId = newId; }); 
      return msg; 
    }
    return null;
  }

  // ─── Tool progress ────────────────────────────────────────────────────────

  @action
  private _updateToolProgress(message: Message, progress: ToolProgressUpdate) {
    if (!message.toolProgress) message.toolProgress = { sitesProcessing: [] };
    switch (progress.type) {
      case 'status':
        if (progress.resetThinking) {
          message.toolProgress.thinkingText = undefined;
        }
        message.toolProgress.status = progress.status; 
        break;
      case 'thinking_chunk':
        if (progress.thinking) {
          message.toolProgress.thinkingText = (message.toolProgress.thinkingText || '') + progress.thinking;
        }
        break;
      case 'text_chunk':
        if (progress.text) {
          message.toolProgress.streamingText = (message.toolProgress.streamingText || '') + progress.text + ' ';
        }
        break;
      case 'word_tokens':
        if (progress.wordTokens) {
          message.toolProgress.wordTokens = progress.wordTokens;
        }
        break;
      case 'search_results': 
        message.toolProgress.searchResults = progress.searchResults; 
        break;
      case 'site_processing':
        if (progress.siteProcessing) {
          const ex = message.toolProgress.sitesProcessing?.find(s => s.link === progress.siteProcessing!.link);
          if (ex) ex.status = progress.siteProcessing.status;
          else message.toolProgress.sitesProcessing = [...(message.toolProgress.sitesProcessing || []), progress.siteProcessing];
        }
        break;
    }
  }

  // ─── Chunk update ─────────────────────────────────────────────────────────

  @action
  private _updateMessageChunk(message: Message, rawData: string): OllamaStreamResponse | null {
    if (!rawData.trim()) return null;
    const json = JSON.parse(rawData) as OllamaStreamResponse;
    if (json.error) { 
      message.errorMessage = json.error; 
      return json; 
    }
    message.content += json.message?.content || '';
    message.thinking += json.message?.thinking || '';
    if (json.message?.tool_calls && json.message.tool_calls.length > 0) {
      if (!message.toolCalls) message.toolCalls = [];
      const offset = message.toolCalls.length;
      const newCalls = json.message.tool_calls.map((tc, i) => ({
        id: tc.id || `${message.id}-tc-${offset + i}`,
        name: tc.function.name,
        arguments: JSON.stringify(tc.function.arguments),
      }));
      message.toolCalls = [...message.toolCalls, ...newCalls];
      // Legacy single-call fields: set from the first tool call only
      if (!message.toolsFunctionName && message.toolCalls.length > 0) {
        message.toolsFunctionName = message.toolCalls[0].name;
        message.toolsFunctionArguments = message.toolCalls[0].arguments;
      }
    }
    if (json.waiting_info !== undefined) message.waitingInfo = json.waiting_info;
    if (json.model_load_completed !== undefined) { 
      message.modelLoadCompleted = json.model_load_completed; 
      message.waitingInfo = undefined; 
    }
    if (json.model_load_total !== undefined) message.modelLoadTotal = json.model_load_total;
    if (json.prompt_eval_completed !== undefined) message.promptEvalCompleted = json.prompt_eval_completed;
    if (json.prompt_eval_total !== undefined) message.promptEvalTotal = json.prompt_eval_total;
    if (json.done) {
      message.tokensIn = json.prompt_eval_count;
      message.tokensOut = json.eval_count;
      message.timeTaken = json.total_duration;
      message.modelLoadCompleted = undefined; message.modelLoadTotal = undefined;
      message.promptEvalCompleted = undefined; message.promptEvalTotal = undefined;
      message.waitingInfo = undefined;
    }
    return json;
  }

  // ─── New chat helper ──────────────────────────────────────────────────────

  async addNewChat(chatId: string): Promise<void> {
    const chat = await apiService.get<ChatResponseItem>(`/api/chats/${chatId}`, { loader: this.chatsLoader });
    runInAction(() => {
      this.chats.unshift({
        id: chatId, model: chat?.model || '', isNameGenerated: chat?.isNameGenerated || false,
        name: chat?.name || 'New Chat', createdAt: +Date.now(), modifiedAt: +Date.now(),
        keywords: chat.keywords || [], temperature: chat.temperature || 0.8,
        thinkMode: chat.thinkMode || 'medium', startFromBeginning: chat.startFromBeginning || false,
        enableSyntaxHighlighting: chat.enableSyntaxHighlighting, tools: chat.tools || [],
        enabledSkills: chat.enabledSkills || [],
        showFullThinking: chat.showFullThinking || false,
        contextLength: chat.contextLength,
        predictLength: chat.predictLength,
        whisperLanguage: chat.whisperLanguage || 'auto'
      });
    });
  }

  // ─── Tool approval ────────────────────────────────────────────────────────

  @action
  onToolApprovalDecision(toolName: string, decision: ToolApprovalDecision) {
    const approval = this.activeSession.pendingToolApprovals.get(toolName);
    if (approval) {
      approval.decision = decision;
      approval.resolve(decision);
    }
    // Send decision to server with chatId for parallel chat support
    wsService.send('chat:tool_decision', { decision, chatId: this.activeChatId });
  }

  // ─── Context compaction ───────────────────────────────────────────────────

  @action
  async compactContext(chatId: string): Promise<void> {
    const session = this.getSession(chatId);
    const conv = this.getConversation(chatId);
    session.isCompacting = true;
    this.generatingChatIds.add(chatId);

    // Create placeholder
    const placeholder: Message = {
      id: 'compact-placeholder',
      role: 'context',
      content: '',
      timestamp: this.getTimestamp(chatId),
      thinking: '',
      parentId: conv[conv.length - 1]?.id,
    };
    conv.push(placeholder);
    session.generatingMessageId = 'compact-placeholder';
    session.currentMessage = conv[conv.length - 1];
    this._autoScrollCallback?.({ force: true });

    // Acquire wake lock and set up loading state
    session.wakeLock = await helperService.getWakeLock();
    session.loading.subscribe(new Promise<void>(resolve => { session._generatingResolve = resolve; }));

    // Trigger compaction via WebSocket (streaming progress handled by chat:chunk + compact_done)
    // Pass selectedMessageId so server builds the chain for the correct branch.
    wsService.send('chat:compact', { chatId, parentMessageId: session.selectedMessageId || undefined });
  }

  @action
  async regenerateCompaction(chatId: string, contextMessageId: string): Promise<void> {
    const session = this.getSession(chatId);
    const conv = this.getConversation(chatId);
    const contextMsg = conv.find(m => m.id === contextMessageId);
    if (!contextMsg) return;

    session.isCompacting = true;
    this.generatingChatIds.add(chatId);

    runInAction(() => {
      contextMsg.content = '';
      session.generatingMessageId = contextMessageId;
      session.currentMessage = contextMsg;
    });
    this._autoScrollCallback?.({ force: true });

    // Acquire wake lock and set up loading state
    session.wakeLock = await helperService.getWakeLock();
    session.loading.subscribe(new Promise<void>(resolve => { session._generatingResolve = resolve; }));

    // Trigger regeneration via WebSocket.
    // parentMessageId = context message's parent (last assistant before compact).
    // Server builds the chain ending there and saves a new context message.
    wsService.send('chat:compact', {
      chatId,
      parentMessageId: contextMsg.parentId || undefined,
    });
  }

  // ─── WebSocket event handlers ──────────────────────────────────────────

  private _setupWsListeners() {
    wsService.on('chat:chunk', this._onChatChunk);
    wsService.on('chat:message_ids', this._onChatMessageIds);
    wsService.on('chat:messages_removed', this._onChatMessagesRemoved);
    wsService.on('chat:new_assistant', this._onNewAssistant);
    wsService.on('chat:tool_start', this._onToolStart);
    wsService.on('chat:tool_progress', this._onToolProgress);
    wsService.on('chat:tool_done', this._onToolDone);
    wsService.on('chat:tool_approval', this._onToolApproval);
    wsService.on('chat:done', this._onChatDone);
    wsService.on('chat:error', this._onChatError);
    wsService.on('chat:restore_state', this._onRestoreState);
    wsService.on('chat:name_generated', this._onNameGenerated);
    wsService.on('chat:compact_start', this._onCompactStart);
    wsService.on('chat:compact_done', this._onCompactDone);
    wsService.on('chat:upcoming', this._onUpcoming);
    wsService.on('chat:upcoming_taken', this._onUpcomingTaken);
    wsService.on('chat:upcoming_removed', this._onUpcomingRemoved);
    wsService.on('ws:disconnect', this._onWsDisconnect);
  }

  /** Server confirmed a queued steering message was persisted. Reconcile the
   *  optimistic local tempId with the real DB id. */
  private _onUpcoming = action((data: unknown) => {
    const d = data as {
      chatId: string;
      messageId: string;
      parentId: string | null;
      tempMessageId: string | null;
      initiatorClientId: string | null;
    };
    const conv = this.getConversation(d.chatId);
    // Only reconcile messages this tab created (avoid clobbering another tab's view).
    if (d.tempMessageId) {
      const local = conv.find(m => m.id === d.tempMessageId);
      if (local) {
        local.id = d.messageId;
        local.parentId = d.parentId ?? undefined;
      }
    }
  });

  /** A queued steering message was picked up by the running loop. Clear its flag so it
   *  moves out of the Upcoming section and into the normal active chain; the loop will
   *  answer it (a fresh assistant placeholder follows via chat:new_assistant). */
  private _onUpcomingTaken = action((data: unknown) => {
    const d = data as { chatId: string; messageId: string; parentId: string };
    const conv = this.getConversation(d.chatId);
    const msg = conv.find(m => m.id === d.messageId);
    if (msg) {
      msg.isUpcoming = false;
      msg.parentId = d.parentId;
    }
    if (d.chatId === this.activeChatId) this._autoScrollCallback?.();
  });

  /** A queued steering message was deleted on the server. Remove it locally (idempotent —
   *  the optimistic splice may have already happened). */
  private _onUpcomingRemoved = action((data: unknown) => {
    const d = data as { chatId: string; messageId: string };
    const conv = this.getConversation(d.chatId);
    const idx = conv.findIndex(m => m.id === d.messageId);
    if (idx !== -1) conv.splice(idx, 1);
  });

  private _onChatChunk = action((data: unknown) => {
    const d = data as {
      chatId: string;
      raw?: string;
      ts?: number;
      waiting_info?: string;
      error?: string;
      model_load_completed?: number;
      model_load_total?: number;
      prompt_eval_completed?: number;
      prompt_eval_total?: number;
      done?: boolean;
      elapsed_ms?: number;
      thinking_time_ms?: number;
    };
    const session = this._findSessionForChat(d.chatId);
    if (!session) return;

    let targetMessage = session.currentMessage;
    if (!targetMessage && session.generatingMessageId) {
      targetMessage = this.getConversation(d.chatId).find(m => m.id === session.generatingMessageId) || null;
      if (targetMessage) session.currentMessage = targetMessage;
    }
    if (!targetMessage) return;

    if (d.raw) {
      // Parse content/thinking before applying to track in token buffer
      const parsed = JSON.parse(d.raw) as OllamaStreamResponse;
      const tokenContent = parsed.message?.content || '';
      const tokenThinking = parsed.message?.thinking || '';
      const ts = d.ts || Date.now();

      // Buffer the token with its timestamp for merge-on-restore
      if (tokenContent || tokenThinking) {
        if (!targetMessage._tokenBuffer) targetMessage._tokenBuffer = [];
        targetMessage._tokenBuffer.push({ ts, content: tokenContent, thinking: tokenThinking });
        targetMessage.lastTokenTimestamp = ts;
      }

      // Track when first thinking token arrived for live thinking timer
      if (tokenThinking && targetMessage._thinkingStartMs === undefined) {
        targetMessage._thinkingStartMs = Date.now();
      }

      this._updateMessageChunk(targetMessage, d.raw);
      if (d.chatId === this.activeChatId) this._autoScrollCallback?.();
    }
    if (d.waiting_info !== undefined) targetMessage.waitingInfo = d.waiting_info;
    if (d.error) targetMessage.errorMessage = d.error;

    // Live timing: update timeTaken from server-measured elapsed time on non-done chunks.
    // On the done chunk, use elapsed_ms (real wall-clock from fetch start) which includes
    // TTTFT and network time to Ollama — more accurate than Ollama's own total_duration.
    if (d.elapsed_ms !== undefined) {
      // On the first token: refine the start time using the server-measured elapsed
      // (corrects for prompt-eval duration and server–client clock drift).
      // Only set once — subsequent chunks would drift further from actual start.
      if (!targetMessage._fetchStartRefined) {
        targetMessage._fetchStartMs = Date.now() - d.elapsed_ms;
        targetMessage._fetchStartRefined = true;
      }
      // Update timeTaken on every chunk (including done) with real wall-clock elapsed
      targetMessage.timeTaken = d.elapsed_ms * 1_000_000;
    }
    // Thinking time sent by server on done chunk (ms since fetch start until last thinking token)
    if (d.thinking_time_ms !== undefined) {
      targetMessage.thinkingTimeTaken = d.thinking_time_ms;
    }

    // Also apply explicit progress fields from the server (fallback when raw chunks
    // do not carry progress updates in a parseable format).
    if (d.model_load_completed !== undefined) {
      targetMessage.modelLoadCompleted = d.model_load_completed;
      targetMessage.waitingInfo = undefined;
    }
    if (d.model_load_total !== undefined) targetMessage.modelLoadTotal = d.model_load_total;
    if (d.prompt_eval_completed !== undefined) targetMessage.promptEvalCompleted = d.prompt_eval_completed;
    if (d.prompt_eval_total !== undefined) targetMessage.promptEvalTotal = d.prompt_eval_total;
    if (d.done) {
      targetMessage.modelLoadCompleted = undefined;
      targetMessage.modelLoadTotal = undefined;
      targetMessage.promptEvalCompleted = undefined;
      targetMessage.promptEvalTotal = undefined;
      targetMessage.waitingInfo = undefined;
      // Clear token buffer on done — no longer needed
      targetMessage._tokenBuffer = undefined;
      targetMessage.lastTokenTimestamp = undefined;
      // Clear live timer anchors — final values come from Ollama's total_duration / thinking_time_ms
      targetMessage._fetchStartMs = undefined;
      targetMessage._fetchStartRefined = undefined;
      targetMessage._thinkingStartMs = undefined;
    }
  });

  private _onChatMessageIds = action((data: unknown) => {
    const d = data as { chatId: string; messageIdMap: Record<string, { id: string; attachmentIds: string[] }>; isNewChat: boolean };

    if (d.isNewChat) {
      const didMigrate = this.migrateNewSession(d.chatId);
      this.addNewChat(d.chatId);

      if (didMigrate) {
        // Initiating tab: session/conversation migrated from 'new' → chatId
        this.activeChatId = d.chatId;
        this.generatingChatIds.add(d.chatId);
      } else {
        // Non-initiating tab: just add to sidebar, don't navigate or mark generating.
        // If the user opens this chat, loadChatMessages will restore running state from the API.
        return;
      }
    }

    const conv = this.getConversation(d.chatId);
    const session = this.getSession(d.chatId);

    for (const [oldId, { id: newId, attachmentIds }] of Object.entries(d.messageIdMap)) {
      const msg = this._updateMessageId(conv, oldId, newId);
      if (msg) {
        msg.attachmentIds = attachmentIds;
        delete msg.images;
      }
      if (oldId === session.generatingMessageId) session.generatingMessageId = newId;
    }

    this._savePromptCallback?.();
    this._refreshModelsCallback?.();
  });

  private _onChatMessagesRemoved = action((data: unknown) => {
    const d = data as { chatId: string; messageIds: string[] };
    const conv = this.getConversation(d.chatId);

    for (const id of d.messageIds) {
      const idx = conv.findIndex(m => m.id === id);
      if (idx !== -1) conv.splice(idx, 1);
    }

  });

  private _onNewAssistant = action((data: unknown) => {
    const d = data as { chatId: string; messageId: string; parentId: string; initiatorClientId?: string | null };
    const conv = this.getConversation(d.chatId);
    const session = this.getSession(d.chatId);

    // Determine ownership: undefined = old server (assume owner), null = scheduled (no owner),
    // string = live chat (owner only if matches our clientId)
    const isOwner = d.initiatorClientId === undefined || d.initiatorClientId === wsService.clientId;

    // Non-owning tab for a chat that isn't currently displayed:
    // just mark as generating for the sidebar animation and stop.
    if (!isOwner && d.chatId !== this.activeChatId) {
      this.generatingChatIds.add(d.chatId);
      return;
    }

    // If there's an existing assistant placeholder (id='a'), replace it in-place
    const placeholder = conv.find(m => m.id === 'a' && m.role === 'assistant');
    if (placeholder) {
      placeholder.id = d.messageId;
      placeholder.parentId = d.parentId;
      session.currentMessage = placeholder;
      session.generatingMessageId = d.messageId;
      this._autoScrollCallback?.({ force: true });
      return;
    }

    // If the parent message (user message) isn't in the conversation, this is a
    // non-initiating tab that doesn't have the full history yet. Load from API
    // which will return the full conversation + running state via the merge logic.
    const hasParent = conv.some(m => m.id === d.parentId);
    if (!hasParent) {
      this.generatingChatIds.add(d.chatId);
      session.generatingMessageId = d.messageId;
      this._loadChatMessagesInternal(d.chatId, {});
      return;
    }

    const msg: Message = {
      id: d.messageId, role: 'assistant', content: '', thinking: '',
      timestamp: this.getTimestamp(d.chatId), parentId: d.parentId,
      modelName: session.currentModel?.name || '',
    };
    // Accumulate time from tool executions in this turn (after the last user message).
    // Excludes prior LLM call times to avoid inflating with model-loading duration.
    const lastUserIdx = conv.reduce((max, m, i) => (m.role === 'user' ? i : max), -1);
    const turnMsgs = conv.slice(lastUserIdx >= 0 ? lastUserIdx + 1 : 0);
    const precedingMs = turnMsgs
      .filter(m => m.role === 'tool')
      .reduce((sum, m) => sum + (m.timeTaken !== undefined ? m.timeTaken / 1_000_000 : 0), 0);
    if (precedingMs > 0) msg._precedingMs = precedingMs;
    // Start the client-side timer immediately so the counter runs during prompt evaluation
    msg._fetchStartMs = Date.now();
    conv.push(msg);
    session.currentMessage = conv[conv.length - 1];
    session.generatingMessageId = d.messageId;
    this._autoScrollCallback?.({ force: true });
  });

  private _onToolStart = action((data: unknown) => {
    const d = data as { chatId: string; toolName: string; arguments: string; operationId: string };
    const conv = this.getConversation(d.chatId);
    const session = this.getSession(d.chatId);

    const toolMessage: Message = {
      id: `t-${d.operationId}`,
      timestamp: this.getTimestamp(d.chatId),
      parentId: session.currentMessage?.id || session.generatingMessageId || undefined,
      role: 'tool',
      content: '',
      thinking: '',
      toolName: d.toolName,
      toolProgress: { operationId: d.operationId, sitesProcessing: [] },
    };
    conv.push(toolMessage);
    session.generatingMessageId = toolMessage.id;
    // Clear the current assistant message — we're now showing the tool
    session.currentMessage = null;
    this._autoScrollCallback?.();
  });

  private _onToolProgress = action((data: unknown) => {
    const d = data as ToolProgressUpdate & { chatId: string; toolName: string; operationId: string };
    const conv = this.getConversation(d.chatId);
    const toolMsg = conv.find(m => m.toolProgress?.operationId === d.operationId);
    if (toolMsg) {
      this._updateToolProgress(toolMsg, d);
      if (d.chatId === this.activeChatId) this._autoScrollCallback?.();
    }
  });

  private _onToolDone = action((data: unknown) => {
    const d = data as {
      chatId: string; toolName: string; isError: boolean; isDenied?: boolean;
      toolContent: string; toolMessageId: string; toolCallId?: string; timeTakenMs?: number;
      assistantMessage?: string; assistantMessageId?: string; assistantAttachmentIds?: string[];
      assistantAttachmentMetas?: { id: string; filename: string; contentType: string; size: number; isImage: boolean; imageWidth?: number | null; imageHeight?: number | null }[];
      sources?: { title: string; link: string; favicon?: string }[];
      toolDataAttachmentId?: string;
    };
    const conv = this.getConversation(d.chatId);
    const session = this.getSession(d.chatId);

    // Find the tool message (by toolName or last tool message)
    const toolMsg = [...conv].reverse().find(m => m.role === 'tool' && m.toolName === d.toolName && m.toolProgress);
    if (toolMsg) {
      const oldToolId = toolMsg.id;
      toolMsg.id = d.toolMessageId;
      if (d.toolCallId) toolMsg.toolCallId = d.toolCallId;
      // Keep generatingMessageId in sync so the next tool's _onToolStart picks the correct parentId
      if (session.generatingMessageId === oldToolId) session.generatingMessageId = d.toolMessageId;
      // Preserve whisper word data before clearing toolProgress
      if (toolMsg.toolProgress?.wordTokens?.length) {
        toolMsg.whisperWords = toolMsg.toolProgress.wordTokens;
      }
      if (d.toolDataAttachmentId) toolMsg.whisperDataAttachmentId = d.toolDataAttachmentId;
      toolMsg.toolProgress = undefined;
      toolMsg.timeTaken = (d.timeTakenMs || 0) * 1_000_000;
      if (d.isError || d.isDenied) {
        toolMsg.errorMessage = d.toolContent || 'Tool execution failed';
        toolMsg.isAborted = d.isDenied;
      } else {
        toolMsg.content = d.toolContent || '';
      }
      if (d.sources?.length) toolMsg.sources = d.sources;
    }

    // If tool returned a direct assistant reply, add it
    if (d.assistantMessageId && d.assistantMessage) {
      const assistantMsg: Message = {
        id: d.assistantMessageId,
        timestamp: this.getTimestamp(d.chatId),
        parentId: d.toolMessageId,
        role: 'assistant',
        content: d.assistantMessage,
        thinking: '',
        attachmentIds: d.assistantAttachmentIds,
        attachmentMetas: d.assistantAttachmentMetas?.map(m => ({ ...m, type: getAttachmentType(m.contentType, m.filename) })),
      };
      conv.push(assistantMsg);
      session.currentMessage = conv[conv.length - 1];
    }

    if (d.chatId === this.activeChatId) this._autoScrollCallback?.();
  });

  private _onToolApproval = action((data: unknown) => {
    const d = data as { chatId: string; toolName: string; serverName: string; arguments: string };
    const conv = this.getConversation(d.chatId);
    const session = this.getSession(d.chatId);

    // Find the last assistant message (the one that requested tools)
    const assistantMsg = [...conv].reverse().find(m => m.role === 'assistant');
    if (assistantMsg) {
      assistantMsg.pendingToolApproval = {
        toolName: d.toolName,
        serverName: d.serverName,
        arguments: d.arguments,
      };
    }

    // Set up a local pending approval so the UI can resolve it
    session.pendingToolApprovals.set(d.toolName, {
      decision: null,
      resolve: () => {}, // The actual decision is sent to server via onToolApprovalDecision
    });
  });

  private _onChatDone = action((data: unknown) => {
    const d = data as { chatId: string; isAborted?: boolean; showFollowUps?: boolean };
    this.generatingChatIds.delete(d.chatId);

    const session = this._findSessionForChat(d.chatId);
    if (!session) return;

    if (d.isAborted && session.currentMessage) {
      session.currentMessage.isAborted = true;
    }

    // Capture before clearing: if _generatingResolve was never set, the session was built purely
    // from WS events (background scheduled chat) without a server-side message load.
    const wasBackgroundSession = !session._generatingResolve;

    session.generatingMessageId = null;
    session.currentMessage = null;
    if (session._generatingResolve) { session._generatingResolve(); session._generatingResolve = null; }
    session.wakeLock?.release();
    session.wakeLock = null;

    if (d.showFollowUps && userService.showFollowUps) {
      session.showFollowUps = true;
      session.followUpKey++;
    }

    // If the active chat was a background scheduled chat whose conversation was built
    // only from incremental WS events (no user messages), reload from server so the
    // user sees the complete conversation instead of partial tool-call-only state.
    if (!d.isAborted && d.chatId === this.activeChatId && wasBackgroundSession) {
      const conv = this.getConversation(d.chatId);
      const hasUserMessages = conv.some(m => m.role === 'user');
      if (!hasUserMessages) {
        this._loadChatMessagesInternal(d.chatId, {});
        return;
      }
    }

    this._autoScrollCallback?.();
    this._refreshModelsCallback?.();
  });

  private _onChatError = action((data: unknown) => {
    const d = data as { chatId: string; error: string };
    this.generatingChatIds.delete(d.chatId);

    const session = this._findSessionForChat(d.chatId);
    if (!session) return;

    if (session.currentMessage) {
      session.currentMessage.errorMessage = d.error;
    }

    session.generatingMessageId = null;
    session.currentMessage = null;
    if (session._generatingResolve) { session._generatingResolve(); session._generatingResolve = null; }
    session.wakeLock?.release();
    session.wakeLock = null;
    this._autoScrollCallback?.();
  });

  private _onWsDisconnect = action(() => {
    // If generations are active, show an error on current messages and end the generations
    if (this.generatingChatIds.size === 0) return;

    for (const chatId of [...this.generatingChatIds]) {
      const session = this._findSessionForChat(chatId);
      if (!session) continue;

      if (session.currentMessage) {
        session.currentMessage.errorMessage = 'Connection lost. The response may be incomplete.';
      }

      session.generatingMessageId = null;
      session.currentMessage = null;
      if (session._generatingResolve) { session._generatingResolve(); session._generatingResolve = null; }
      session.wakeLock?.release();
      session.wakeLock = null;
    }
    this.generatingChatIds.clear();
    this._autoScrollCallback?.();
  });

  private _onRestoreState = action((data: unknown) => {
    if (!data) return;
    const d = data as {
      chatId: string | null;
      phase: string;
      currentMessage: {
        content: string; thinking: string; modelName: string;
        messageId?: string;
        lastTokenTimestamp?: number;
        toolCalls?: { id: string; name: string; arguments: string }[];
        tokensIn?: number; tokensOut?: number; timeTaken?: number;
        modelLoadCompleted?: number; modelLoadTotal?: number;
        promptEvalCompleted?: number; promptEvalTotal?: number;
        waitingInfo?: string;
      } | null;
      toolProgress: { toolName: string; operationId: string; status?: string; streamingText?: string } | null;
    };
    if (!d.chatId) return;

    // Restore generation state
    this.generatingChatIds.add(d.chatId);
    const session = this.getSession(d.chatId);

    if (d.currentMessage) {
      const conv = this.getConversation(d.chatId);
      // Find the assistant message being generated by its messageId (DB id)
      let lastAssistant = d.currentMessage.messageId
        ? conv.find(m => m.id === d.currentMessage!.messageId)
        : [...conv].reverse().find(m => m.role === 'assistant');
      if (!lastAssistant) {
        lastAssistant = {
          id: d.currentMessage.messageId || 'restoring', role: 'assistant', content: '', thinking: '',
          timestamp: this.getTimestamp(d.chatId),
          modelName: d.currentMessage.modelName,
        };
        conv.push(lastAssistant);
        lastAssistant = conv[conv.length - 1];
      }

      // Merge: only apply server content if it's ahead of what we already have
      const serverTs = d.currentMessage.lastTokenTimestamp || 0;
      const localTs = lastAssistant.lastTokenTimestamp || 0;
      if (serverTs >= localTs) {
        lastAssistant.content = d.currentMessage.content;
        lastAssistant.thinking = d.currentMessage.thinking || '';
        lastAssistant.lastTokenTimestamp = serverTs;
        // Clear token buffer since we just got a full snapshot from server
        lastAssistant._tokenBuffer = [];
      }
      lastAssistant.modelName = d.currentMessage.modelName;
      if (d.currentMessage.toolCalls) {
        lastAssistant.toolCalls = d.currentMessage.toolCalls;
        lastAssistant.toolsFunctionName = d.currentMessage.toolCalls[0]?.name;
        lastAssistant.toolsFunctionArguments = d.currentMessage.toolCalls[0]?.arguments;
      }
      if (d.currentMessage.tokensIn) lastAssistant.tokensIn = d.currentMessage.tokensIn;
      if (d.currentMessage.tokensOut) lastAssistant.tokensOut = d.currentMessage.tokensOut;
      if (d.currentMessage.modelLoadCompleted !== undefined) lastAssistant.modelLoadCompleted = d.currentMessage.modelLoadCompleted;
      if (d.currentMessage.modelLoadTotal !== undefined) lastAssistant.modelLoadTotal = d.currentMessage.modelLoadTotal;
      if (d.currentMessage.promptEvalCompleted !== undefined) lastAssistant.promptEvalCompleted = d.currentMessage.promptEvalCompleted;
      if (d.currentMessage.promptEvalTotal !== undefined) lastAssistant.promptEvalTotal = d.currentMessage.promptEvalTotal;
      if (d.currentMessage.waitingInfo) lastAssistant.waitingInfo = d.currentMessage.waitingInfo;

      session.currentMessage = lastAssistant;
      session.generatingMessageId = lastAssistant.id;
      // Start loading state with a resolvable promise (guard: if _loadChatMessagesInternal already
      // subscribed one first due to a loadChats race, reuse that promise — chat:done resolves it)
      if (!session._generatingResolve) {
        session.loading.subscribe(new Promise<void>((resolve) => { session._generatingResolve = resolve; }));
      }
    }

    if (d.toolProgress) {
      const conv = this.getConversation(d.chatId);
      let toolMsg = [...conv].reverse().find(m => m.role === 'tool' && m.toolProgress?.operationId === d.toolProgress!.operationId);
      if (!toolMsg) {
        // Tool message not yet in the conversation (e.g. page refreshed very early) — create a placeholder
        toolMsg = {
          id: `t-${d.toolProgress.operationId}`,
          timestamp: this.getTimestamp(d.chatId),
          role: 'tool',
          content: '',
          thinking: '',
          toolName: d.toolProgress.toolName,
          toolProgress: { operationId: d.toolProgress.operationId, sitesProcessing: [] },
        };
        conv.push(toolMsg);
        toolMsg = conv[conv.length - 1];
      }
      if (!toolMsg.toolProgress) toolMsg.toolProgress = { operationId: d.toolProgress.operationId, sitesProcessing: [] };
      if (d.toolProgress.status) toolMsg.toolProgress.status = d.toolProgress.status;
      if (d.toolProgress.streamingText) toolMsg.toolProgress.streamingText = d.toolProgress.streamingText;
      session.generatingMessageId = toolMsg.id;
      session.currentMessage = null;
    }

    // If this chat isn't currently displayed, switch to it
    if (this.activeChatId !== d.chatId) {
      this.activeChatId = d.chatId;
      this._loadChatMessagesInternal(d.chatId, {});
    }

    this._autoScrollCallback?.();
  });

  private _onNameGenerated = action((data: unknown) => {
    const d = data as { chatId: string; name: string; keywords: string | string[] };
    const chat = this.chats.find(c => c.id === d.chatId);
    if (chat) {
      chat.name = d.name;
      chat.keywords = Array.isArray(d.keywords)
        ? d.keywords
        : (d.keywords ? d.keywords.split(',').map(k => k.trim()).filter(Boolean) : []);
      chat.isNameGenerated = true;
    }
    this._refreshModelsCallback?.();
  });

  private _onCompactStart = action((data: unknown) => {
    const d = data as { chatId: string };
    const session = this._findSessionForChat(d.chatId);
    if (!session) return;
    session.isCompacting = true;

    // If already set up by client-initiated compact/regenerate, skip creating placeholder
    if (session.generatingMessageId) return;

    if (!this.isGenerating(d.chatId)) this.generatingChatIds.add(d.chatId);

    // Create placeholder for server-triggered compact
    const conv = this.getConversation(d.chatId);
    const placeholder: Message = {
      id: 'compact-placeholder',
      role: 'context',
      content: '',
      timestamp: this.getTimestamp(d.chatId),
      thinking: '',
      parentId: conv[conv.length - 1]?.id,
    };
    conv.push(placeholder);
    session.generatingMessageId = 'compact-placeholder';
    session.currentMessage = conv[conv.length - 1];
    this._autoScrollCallback?.({ force: true });
  });

  private _onCompactDone = action((data: unknown) => {
    const d = data as {
      chatId: string;
      message?: { id: string; role: string; content: string; previousMessageIds?: string[]; compactedMessageIds?: string[]; modelName?: string; timestamp: number; parentId?: string; tokensIn?: number; tokensOut?: number };
      error?: string;
      isAborted?: boolean;
    };
    const session = this._findSessionForChat(d.chatId);
    // Capture before clearing — needed to find the regenerated entry by its old ID
    const oldGeneratingId = session?.generatingMessageId;
    if (session) {
      session.isCompacting = false;
      session.generatingMessageId = null;
      session.currentMessage = null;
      if (session._generatingResolve) { session._generatingResolve(); session._generatingResolve = null; }
      session.wakeLock?.release();
      session.wakeLock = null;
    }
    this.generatingChatIds.delete(d.chatId);
    const conv = this.getConversation(d.chatId);

    const placeholderIdx = conv.findIndex(m => m.id === 'compact-placeholder');
    if (placeholderIdx !== -1) {
      // Fresh compact: replace placeholder with real message or remove on abort/error
      if (d.message) {
        conv[placeholderIdx] = {
          id: d.message.id,
          role: 'context',
          content: d.message.content,
          previousMessageIds: d.message.previousMessageIds,
          compactedMessageIds: d.message.compactedMessageIds,
          modelName: d.message.modelName,
          timestamp: d.message.timestamp,
          parentId: d.message.parentId,
          tokensIn: d.message.tokensIn,
          tokensOut: d.message.tokensOut,
          thinking: '',
        } as Message;
      } else {
        conv.splice(placeholderIdx, 1);
      }
    } else if (d.message && oldGeneratingId) {
      // Regenerate: existing context entry was used as the placeholder.
      // The server saved a new message (new ID) — update the entry in place.
      const existing = conv.find(m => m.id === oldGeneratingId);
      if (existing) {
        existing.id = d.message.id;
        existing.content = d.message.content;
        existing.tokensIn = d.message.tokensIn;
        existing.tokensOut = d.message.tokensOut;
        existing.modelName = d.message.modelName;
        existing.timestamp = d.message.timestamp;
        existing.previousMessageIds = d.message.previousMessageIds;
        existing.compactedMessageIds = d.message.compactedMessageIds;
      }
    }
    this._autoScrollCallback?.();
  });

  /** Find the session for a given chatId (could be active or background) */
  private _findSessionForChat(chatId: string): ChatSession | null {
    if (this._sessions.has(chatId)) return this._sessions.get(chatId)!;
    // Check if 'new' session was just migrated
    if (this._sessions.has('new') && !this._conversations.has(chatId)) return this._sessions.get('new')!;
    return null;
  }

  // ─── Generate chat name ───────────────────────────────────────────────────

  async generateChatName(chatId: string, serverKey?: string): Promise<void> {
    const resp = await apiService.post<{ name: string; keywords: string }>(`/api/chats/${chatId}/generate-name`, null, { 
      headers: { 
        'x-ollama-server': serverKey ?? '' 
      } 
    });
    // Eagerly apply the response in case the WS event was missed (e.g. WS reconnect during generation)
    if (resp?.name) {
      runInAction(() => {
        const chat = this.chats.find(c => c.id === chatId);
        if (chat) {
          chat.name = resp.name;
          chat.keywords = resp.keywords ? resp.keywords.split(',').map(k => k.trim()).filter(Boolean) : [];
          chat.isNameGenerated = true;
        }
      });
    }
  }

  // ─── Generate response (main loop) ───────────────────────────────────────

  @action
  async generateResponse(
    capturedChatId: string | null,
    chat: ChatResponseItem | null,
    options: { updateTitle?: boolean; skipMessageId?: string; source?: 'image' } = {}
  ): Promise<void> {
    // Ensure user settings (default tools, prompt, etc.) are loaded before any generation
    await userService.waitForSettings();

    const session = this.getSession(capturedChatId);
    const conv_arr = this.getConversation(capturedChatId);
    const isNewChat = !capturedChatId;

    // Pin the current model into the session so background generation keeps its model
    if (!session.currentModel && this.currentModel) {
      session.currentModel = this.currentModel;
    }

    const currentModel = session.currentModel || this.currentModel;

    if (capturedChatId) this.generatingChatIds.add(capturedChatId);
    session.showFollowUps = false;

    // Determine parentMessageId and payload data
    let parentMessageId: string | undefined;
    let userMessage: string | undefined;
    let documents: string[] | undefined;
    let images: string[] | undefined;
    let existingAttachmentIds: string[] | undefined;
    let systemMessage: string | undefined;
    let isToolReExecution = false;

    if (options.skipMessageId) {
      // Regeneration: parentMessageId = parent of the skipped message
      const skipMsg = conv_arr.find(m => m.id === options.skipMessageId);
      parentMessageId = skipMsg?.parentId;

      // Include the system message so edits (or a newly-added, not-yet-saved 's' one) are persisted before regeneration
      const sysMsg = conv_arr.find(m => m.role === 'system');
      if (sysMsg) systemMessage = sysMsg.content;

      // Detect tool re-execution for UI placeholder purposes
      if (parentMessageId) {
        const parent = conv_arr.find(m => m.id === parentMessageId);
        isToolReExecution = !!parent && parent.role === 'assistant' &&
          !!((parent.toolCalls && parent.toolCalls.length > 0) || parent.toolsFunctionName);
      }
    } else {
      // New message: extract data from the user message (id='u') pushed by sendMessage
      const userMsg = conv_arr.find(m => m.id === 'u' && m.role === 'user');
      if (userMsg) {
        userMessage = userMsg.content;
        images = userMsg.images;
        parentMessageId = userMsg.parentId;
        if (userMsg.attachmentIds && userMsg.attachmentIds.length > 0) {
          existingAttachmentIds = userMsg.attachmentIds;
        }
        if (userMsg._documentFiles) {
          documents = await Promise.all(userMsg._documentFiles.map(async (f: File) => {
            const b64 = await imageService.readFileAsBase64(f);
            const fileType = f.type || 'application/octet-stream';
            return `${f.name}:${fileType}:${b64}`;
          }));
        }

        // When the parent is a temp system message (id='s', not yet saved to DB),
        // extract its content so the server can persist it first and use its real
        // DB id as the user message's parentId.  Without this, the server receives
        // parentMessageId='s' which is not a valid MongoDB ObjectId and crashes.
        if (parentMessageId === 's') {
          const tempSysMsg = conv_arr.find(m => m.id === 's' && m.role === 'system');
          systemMessage = tempSysMsg?.content ?? '';
          parentMessageId = undefined; // server derives parentId from the saved system message
        }

      } else {
        // No user message with temp id — messages already saved (e.g., image generation flow)
        // Use the last message in the chain as parentMessageId
        const conversation = this.getMessageChain(capturedChatId);
        const lastMsg = conversation[conversation.length - 1];
        parentMessageId = lastMsg?.id;
      }
    }

    // Create UI placeholders
    if (isToolReExecution) {
      // Tool re-execution: server will use the existing assistant.
      // No new placeholder — just mark the existing assistant as generating.
      const assistantMsg = conv_arr.find(m => m.id === parentMessageId);
      if (assistantMsg) {
        session.generatingMessageId = assistantMsg.id;
      }
    } else if (options.source === 'image') {
      // Image/video generation: the assistant message with tool calls is already pre-saved
      // in conv_arr by _generateImage/_generateVideo before this call.
      // Pushing an 'a' placeholder would leave a stale entry that corrupts the parent
      // chain on subsequent generations and causes the chat to mis-scroll (top instead
      // of bottom). Instead, mark the last conv message (assistant-with-toolcall) as
      // generating so _onToolStart picks up the correct parentId.
      const lastConvMsg = conv_arr[conv_arr.length - 1];
      if (lastConvMsg) {
        session.generatingMessageId = lastConvMsg.id;
      }
      // Force scroll to absolute bottom so the new request is visible.
      // forceBottom bypasses the isAtBottom check (new messages just increased
      // scrollHeight so isAtBottom would be false) and uses a simple scrollTo(scrollHeight)
      // rather than the formula that positions the last element's top at the viewport.
      this._autoScrollCallback?.({ forceBottom: true });
    } else {
      // Create assistant placeholder for loading state
      const lastConvMsg = conv_arr[conv_arr.length - 1];
      if (lastConvMsg) {
        const assistantMsg: Message = {
          id: 'a', role: 'assistant',
          content: '',
          thinking: '',
          timestamp: this.getTimestamp(capturedChatId),
          parentId: options.skipMessageId
            ? parentMessageId || lastConvMsg.id
            : lastConvMsg.id,
          modelName: currentModel?.name || '',
        };
        conv_arr.push(assistantMsg);
        session.currentMessage = conv_arr[conv_arr.length - 1];
        session.generatingMessageId = 'a';
        this._autoScrollCallback?.({ force: true });
      }
    }

    // Acquire wake lock
    session.wakeLock = await helperService.getWakeLock();

    // Keep loading state active until chat:done or chat:error resolves it
    session.loading.subscribe(new Promise<void>((resolve) => { session._generatingResolve = resolve; }));

    // Resolve settings
    const chatItem = capturedChatId ? this.chats.find(c => c.id === capturedChatId) : null;
    const contextLength = chatItem?.contextLength || session.chatSettings?.contextLength || userService.defaultContextLength || null;
    const predictLength = chatItem?.predictLength || session.chatSettings?.predictLength || null;
    const _parseKTokens = (v: string | null): number | null => {
      if (!v || v === 'auto') return null;
      const n = parseInt(v, 10);
      return isNaN(n) ? null : n * 1024;
    };

    // Send start signal via WebSocket
    wsService.send('chat:start', {
      chatId: capturedChatId,
      model: currentModel?.name,
      serverKey: currentModel?.server,
      parentMessageId,
      userMessage,
      documents,
      images,
      existingAttachmentIds,
      systemMessage,
      settings: {
        temperature: chat?.temperature || session.chatSettings?.temperature || 0.8,
        thinkMode: chat?.thinkMode || session.chatSettings?.thinkMode || null,
        contextLength: contextLength ? parseInt(contextLength, 10) : null,
        predictLength: _parseKTokens(predictLength),
        tools: chat?.tools ?? session.chatSettings?.tools ?? userService.defaultTools ?? true,
        enabledSkills: chat?.enabledSkills ?? session.chatSettings?.enabledSkills ?? [],
        enableSyntaxHighlighting: session.chatSettings?.enableSyntaxHighlighting ?? userService.defaultEnableSyntaxHighlighting,
        showFullThinking: session.chatSettings?.showFullThinking || userService.defaultShowFullThinking,
        whisperLanguage: session.chatSettings?.whisperLanguage || userService.defaultWhisperLanguage || 'auto',
      },
      source: options.source,
      updateTitle: options.updateTitle || isNewChat,
      clientId: wsService.clientId,
    });
  }

  // ─── sendMessage (called from Chat prompt area) ──────────────────────────

  @action
  async sendMessage(msg: string, attachmentFiles: File[], startFromBeginning: boolean): Promise<void> {
    const chatId = this.activeChatId;
    const chat = chatId ? this.chats.find(c => c.id === chatId) || null : null;
    const session = this.getSession(chatId);

    const conversation = this.getMessageChain(chatId);
    let parentMessage = conversation[conversation.length - 1];
    if (startFromBeginning) {
      parentMessage = conversation.find(m => m.role === 'system' || m.role === 'root')!;
    }
    const message: Message = {
      id: 'u', 
      parentId: parentMessage?.id, 
      role: 'user', 
      content: msg, 
      timestamp: this.getTimestamp(chatId) 
    };
    const imageFiles = attachmentFiles.filter(f => f.type.startsWith('image/'));
    const documentFiles = attachmentFiles.filter(f => !f.type.startsWith('image/'));
    // Fall back to the globally-selected model when session.currentModel hasn't been
    // set yet (e.g. right after switching chats before the model callback fires).
    const currentModel = session.currentModel || this.currentModel;
    if (imageFiles.length > 0 && (currentModel?.capabilities?.includes('vision') || currentModel?.capabilities?.includes('tools'))) {
      message.images = await Promise.all(imageFiles.map(f => imageService.readFileAsBase64(f)));
    }
    if (documentFiles.length > 0) {
      message._documentFiles = documentFiles;
    }
    const conv = this.getConversation(chatId);
    conv.push(message);
    this.generateResponse(chatId, chat);
  }

  // ─── updateMessageId (public for Chat.tsx use) ────────────────────────────

  updateMessageIdInConversation(chatId: string | null, oldId: string, newId: string): Message | null {
    return this._updateMessageId(this.getConversation(chatId), oldId, newId);
  }

  // ─── New chat setup ───────────────────────────────────────────────────────

  @action
  startNewChat(defaultSettings: ChatResponseItemSettings | null) {
    const session = this.getSession(null);
    session.chatSettings = defaultSettings;
    session.isAlternativePromptMode = false;
    session.showFollowUps = false;
    this.activeChatId = null;
    const conv = this.getConversation(null);
    conv.length = 0;
    // Reset 'new' session for fresh start
    const newSession = this._sessions.get('new');
    if (newSession) {
      newSession.chatSettings = defaultSettings;
      newSession.isAlternativePromptMode = false;
      newSession.showFollowUps = false;
    }
  }
}

export const chatExecutionService = new ChatExecutionService();
